import { Collapse, Table, Space, Typography } from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { getOrganizationMainRoles } from "@/components/Roles";
import UserOrganizationHeader from "@/components/UserOrganizationHeader";
import UserRoleRemoveButton from "@/components/UserRoleRemoveButton";

const { Title } = Typography;

const TabRoleAndPermissions = ({ user, loading }) => {
  const { t } = useTranslation();

  if (!user) return null;

  const userRoles = user.roles ?? [];
  const mainRoles = getOrganizationMainRoles({
    roles: userRoles,
    organizationId: user.organization_id,
  });
  const mainRole = mainRoles.length ? mainRoles[0] : null;
  // remove main role from the list of roles
  const filteredRoles = userRoles.filter((role) => role.id !== mainRole?.id);

  const filterBySubjectType = function (roles, subjectType) {
    const filteredRoles =
      loading || !roles
        ? []
        : Object.values(roles).filter(
            (role) => role.subject_type == subjectType
          );
    return filteredRoles;
  };

  const roleColumns = function (type) {
    return [
      {
        title: t("user:role"),
        dataIndex: "behavior",
        render: (behavior) => behavior.split("\\").slice(-1)[0],
        key: "role",
      },
      {
        title: t("global:" + type),
        dataIndex: ["subject", "name"],
        key: "name",
      },
      {
        title: "",
        dataIndex: "behavior",
        render: (_behavior, role) => {
          return <UserRoleRemoveButton role={role} userId={user.id} />;
        },
        key: "button",
      },
    ];
  };

  const items = [
    {
      key: "organizations",
      label: t("global:organizationPlural"),
      children: (
        <Table
          rowKey="id"
          loading={loading}
          dataSource={filterBySubjectType(
            filteredRoles,
            "App\\Models\\Organization"
          )}
          columns={roleColumns("organization")}
        />
      ),
    },
    {
      key: "locations",
      label: t("global:locationPlural"),
      children: (
        <Table
          rowKey="id"
          loading={loading}
          dataSource={filterBySubjectType(
            filteredRoles,
            "App\\Models\\Location"
          )}
          columns={roleColumns("location")}
        />
      ),
    },
    {
      key: "assets",
      label: t("global:assetPlural"),
      children: (
        <Table
          rowKey="id"
          loading={loading}
          dataSource={filterBySubjectType(filteredRoles, "App\\Models\\Asset")}
          columns={roleColumns("asset")}
        />
      ),
    },
    {
      key: "interventions",
      label: t("global:interventionPlural"),
      children: (
        <Table
          rowKey="id"
          loading={loading}
          dataSource={filterBySubjectType(
            filteredRoles,
            "App\\Models\\Intervention"
          )}
          columns={roleColumns("intervention")}
        />
      ),
    },
  ];

  return (
    <Space orientation="vertical" style={{ width: "100%" }}>
      <UserOrganizationHeader
        mainRole={mainRole}
        user={user}
        loading={loading}
      />
      <Title level={4}>{t("user:otherPermissions")}</Title>
      <Collapse
        accordion
        defaultActiveKey="organizations"
        expandIconPlacement="end"
        items={items}
      />
    </Space>
  );
};

TabRoleAndPermissions.propTypes = {
  user: PropTypes.object,
  loading: PropTypes.bool,
};

export default TabRoleAndPermissions;
